import { useEffect, useState } from "react";
import { apiFetch, FetchTypes } from "../../api/core";
import { useFetch } from "../../api/useFetch"
import { useWithPromiseLoading } from "../../api/useWithPromiseLoading";
import { useForm } from "../../hooks/useForm";
import { DashboardConfig, WidgetConfig } from "../Dashboard/types";
import { DashItem } from "./types"

const selectData = {
    selector: 'racnumbers' as null | string,
}

export const useEditDash = () => {
    const { data, loading, reload } = useFetch<DashItem[]>([], { url: '/dashboard' });
    const selectForm = useForm(selectData);
    const actions = useFetch(null, {});

    return {
        data, 
        loading: loading || actions.loading,
        selectForm,
        add: (body: { code: string, title: string }) => {
            actions.request({ method: FetchTypes.POST, url: '/dashboard', body }).then(reload);
        },
    }
}

const newWidget = {
    title: '',
    widgettype: null as null | string,
}

const WidgetApiPath = "/api/dashboard/widget/";

export const useEditForm = (code: string) => {
    const {loading, data, setData, reload} = useFetch<null | DashboardConfig>(null, { url: '/dashboard/' + code });
    const actions = useFetch<any>(null, { url: '/dashboard/' + code});
    const form = useForm(newWidget, { required: ['title', 'widgettype'] });
    const widgetLoading = useWithPromiseLoading();

    const [editedWidget, setEditedWidget] = useState<WidgetConfig | null>();

    useEffect(() => {
        if (actions.data) {
            setData(actions.data);
        }
    }, [actions.data, setData]);

    const save = () => {
        if (data) {
            return actions
                .request({ 
                    method: FetchTypes.PUT,
                    body: {
                        params_defaults: data.params.reduce((acc, param) => ({...acc, [param.code]: param.default}), {}),
                        title: data.title,
                        code: data.code,
                        query_substitutions: data.query_substitutions || "",
                        widgets_ids: data.widgets.map(w => w._id)
                    }
                });
        }

        return new Promise(() => {});
    }

    return {
        loading: loading || actions.loading,
        widgetLoading: widgetLoading.isLoading,
        form,
        data,
        setData,
        add: () => {
            // для сохранения текущего положения виджетов
            const { title, widgettype } = form.state;
            save().then(() => {
                actions
                    .request({ 
                        method: FetchTypes.POST,
                        url: `/dashboard/${data?.code}/widget`,
                        body: { title, widgettype }
                    });
                
                form.setState({ title: '', widgettype: null });
            });
        },
        addExisting: (widget: WidgetConfig) => {
            save().then(() => {
                actions
                    .request({ 
                        method: FetchTypes.POST,
                        url: `/dashboard/${data?.code}/widget/${widget._id}`,
                    });
            });
        },
        duplicateWidget: (widget: WidgetConfig) => {
            const duplicated: Partial<WidgetConfig> = { ...widget, _id: undefined, title: `${widget.title} (copy)` };
            save().then(() => {
                actions
                    .request({ 
                        method: FetchTypes.POST,
                        url: `/dashboard/${data?.code}/widget`,
                        body: duplicated,
                    });
            });
        },
        save,
        moveWidget: (targetWidget: WidgetConfig, to: number) => {
            if (data && to >= 0 && to <= data.widgets.length) {
                const index = data.widgets.findIndex(w => w._id === targetWidget._id) > to ? to : to - 1;

                const newlist: typeof data.widgets = [];

                for (const widget of data.widgets) {
                    if (newlist.length === index) {
                        newlist.push(targetWidget);
                    }

                    if (widget._id !== targetWidget._id) {
                        newlist.push(widget);
                    }
                }

                if (newlist.length === index) { 
                    newlist.push(targetWidget);
                }

                setData({...data, widgets: newlist});
            }
        },
        saveWidget: ({_id, ...data}: WidgetConfig) => {
            widgetLoading.withLoading(
                apiFetch<WidgetConfig>(`${WidgetApiPath}/${_id}`, "put", data)
                    .then((widget: WidgetConfig) => {
                        setEditedWidget(w => widget && w?._id === widget._id ? widget : w);
                        reload();
                    }));
        },
        removeWidget: (id: string) => {
            widgetLoading.withLoading(
                apiFetch<WidgetConfig>(`${WidgetApiPath}/${id}`, "delete")
                    .then(reload));
        },

        selectWidgetForEditing: (widget: WidgetConfig | null) => {
            setEditedWidget(widget);
        },
        editedWidget,
    }
}